<script setup>
import AppLayout from '@/Layouts/AppLayout.vue'
import PrimaryButton from '@/Components/PrimaryButton.vue'
import { ref, computed } from 'vue'
import { defineProps } from 'vue'
import { Inertia } from '@inertiajs/inertia'
import DialogModal from '@/Components/DialogModal.vue'
import SelectInput from '@/Components/SelectInput.vue'
import { useTenantTextRules } from '@/helpers'

const props = defineProps({
    colors: Array,
    fabrics: Array,
    garments: Array,
    sizes: Array,
    locations: Array,
    materialCodes: Array,
    departments: Array,
    styleNumbers: Array,
    locationDetails: Array,
    styleNumberExtendedView: {
        type: Boolean,
        default: false,
    },
})
const { resolveTextRule } = useTenantTextRules();

const showDeleteModal = ref(false)
const itemToDelete = ref(null)

const confirmDelete = (item) => {
    itemToDelete.value = item
    showDeleteModal.value = true
}
const closeDeleteModal = () => {
    itemToDelete.value = null
    showDeleteModal.value = false
}
const performDelete = () => {
    if (!itemToDelete.value) return
    Inertia.delete(route('admin.database.destroy', {
        type: activeTab.value,
        id: itemToDelete.value.id,
    }), {
        preserveScroll: true,
        onSuccess: () => closeDeleteModal(),
    })
}

const defaultTabLabels = {
    color: 'Colors',
    garment: 'Garment',
    fabric: 'Fabric',
    size: 'Size Scale',
    location: 'Locations',
    materialCode: 'Material Codes',
    department: 'Departments',
    styleNumber: 'Style',
}

const textRuleKeyByTab = {
    color: 'database.tabs.color',
    garment: 'database.tabs.garment',
    fabric: 'database.tabs.fabric',
    size: 'database.tabs.size',
    location: 'database.tabs.location',
    materialCode: 'database.tabs.material_code',
    department: 'database.tabs.department',
    styleNumber: 'database.tabs.style_number',
}

const formatTabLabel = (tab) => {
    if (!tab) return '';

    const fallback = defaultTabLabels[tab]
        || tab.replace(/([A-Z])/g, ' $1').replace(/^./, (m) => m.toUpperCase());
    const ruleKey = textRuleKeyByTab[tab];

    if (!ruleKey) {
        return fallback;
    }

    return resolveTextRule(ruleKey, fallback, ruleKey);
};

const resolveColumnLabel = (tab, key) => {
    const normalizedKey = String(key || '');

    const byTabAndKey = {
        color: {
            name: 'Color Name',
            hex: 'Hex',
            unicode: 'Unicode',
            pms: 'PMS',
        },
        garment: {
            name: `${formatTabLabel('garment')} Name`,
            description: 'Description',
            unicode: 'Unicode',
            imageURL: 'Image URL',
        },
        fabric: {
            name: `${formatTabLabel('fabric')} Name`,
            description: 'Description',
            unicode: 'Unicode',
            imageURL: 'Image URL',
        },
        size: {
            name: `${formatTabLabel('size')} Name`,
            row: 'Row',
            column: 'Column',
        },
        location: {
            name: `${formatTabLabel('location')} Name`,
            identifier: 'Identifier',
        },
        materialCode: {
            code: formatTabLabel('materialCode'),
        },
        department: {
            department: 'Department Name',
        },
        styleNumber: {
            image: 'Image',
            number: 'Style Number',
            fabric: 'Fabric',
            rental_price_daily: 'Daily',
            rental_price_weekly: 'Weekly',
            rental_price_monthly: 'Monthly',
            notes: 'Notes',
        },
    };

    const fallback = byTabAndKey?.[tab]?.[normalizedKey]
        || normalizedKey.replace(/([A-Z])/g, ' $1').replace(/^./, (m) => m.toUpperCase());
    const ruleKey = `database.columns.${tab}.${normalizedKey}`;

    return resolveTextRule(ruleKey, fallback, ruleKey);
};

const activeTab = ref(new URL(window.location.href).searchParams.get('tab') || 'color');
const changeTab = (tab) => {
    activeTab.value = tab;
    const url = new URL(window.location.href);
    url.searchParams.set('tab', tab);
    window.history.replaceState({}, '', url.toString());
};

const items = () => {
    switch (activeTab.value) {
        case 'color': return props.colors
        case 'fabric': return props.fabrics
        case 'garment': return props.garments
        case 'size': return props.sizes
        case 'location': return props.locations
        case 'materialCode': return props.materialCodes
        case 'department': return props.departments
        case 'styleNumber': return props.styleNumbers
        default: return []
    }
}

const styleNumberColumnKeys = [
    'image',
    'number',
    'fabric',
    'rental_price_daily',
    'rental_price_weekly',
    'rental_price_monthly',
    'notes',
]

const visibleKeys = () => {
    if (activeTab.value === 'styleNumber' && props.styleNumberExtendedView) {
        return styleNumberColumnKeys
    }

    const sample = items()[0]
    if (!sample) return []
    return Object.keys(sample).filter(key => !key.toLowerCase().endsWith('id'))
}

const visibleItems = () => {
    return items().map(item => {
        const clone = {}
        for (const key of visibleKeys()) clone[key] = item[key]
        return clone
    })
}

const goToCreate = () => {
    const tab = activeTab.value
    const query = tab === 'styleNumber' && props.styleNumberExtendedView
        ? `?return_to=${encodeURIComponent(`${route('admin.database.index')}?tab=${tab}`)}`
        : ''

    Inertia.visit(`/admin/database/create/${tab}${query}`)
}

const canDeleteActiveTab = computed(() => {
    if (activeTab.value === 'styleNumber') {
        return props.styleNumberExtendedView
    }

    return activeTab.value !== 'styleNumber'
})

const formatCellValue = (tab, key, value) => {
    if (tab === 'styleNumber' && props.styleNumberExtendedView) {
        if (['rental_price_daily', 'rental_price_weekly', 'rental_price_monthly'].includes(key)) {
            if (value === null || value === undefined || value === '') {
                return ''
            }

            const amount = Number(value)
            if (Number.isNaN(amount)) {
                return value
            }

            return amount.toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            })
        }
    }

    return value ?? ''
}

const deleteItemLabel = computed(() => {
    const item = itemToDelete.value
    if (!item) return 'this item'

    return item.name || item.number || item.code || item.department || 'this item'
})

const normalizedLocationDetailsMap = computed(() => {
    const map = new Map();

    if (Array.isArray(props.locations)) {
        props.locations.forEach((loc) => {
            const locId = String(loc.id ?? loc.locationId ?? '');
            if (!locId) return;
            const details = Array.isArray(loc.details) ? loc.details : [];
            if (!map.has(locId)) map.set(locId, []);
            details.forEach((d) => {
                map.get(locId).push({
                    id: d.id,
                    locationId: String(d.locationId ?? locId),
                    warehouse: d.warehouse ?? null,
                    container: d.container ?? null,
                    conveyor: d.conveyor ?? null,
                });
            });
        });
    }

    if (
        props.locationDetails &&
        !Array.isArray(props.locationDetails) &&
        typeof props.locationDetails === 'object'
    ) {
        Object.entries(props.locationDetails).forEach(([key, value]) => {
            const locId = String(key);
            const arr = Array.isArray(value) ? value : [value];
            if (!map.has(locId)) map.set(locId, []);
            arr.forEach((d) => {
                map.get(locId).push({
                    id: d.id,
                    locationId: String(d.locationId ?? locId),
                    warehouse: d.warehouse ?? null,
                    container: d.container ?? null,
                    conveyor: d.conveyor ?? null,
                });
            });
        });
    }

    if (Array.isArray(props.locationDetails)) {
        props.locationDetails.forEach((value, index) => {
            if (!value) return; 
            const locId = String(index);
            const arr = Array.isArray(value) ? value : [value];
            if (!map.has(locId)) map.set(locId, []);
            arr.forEach((d) => {
                map.get(locId).push({
                    id: d.id,
                    locationId: String(d.locationId ?? locId),
                    warehouse: d.warehouse ?? null,
                    container: d.container ?? null,
                    conveyor: d.conveyor ?? null,
                });
            });
        });
    }

    for (const [k, arr] of map.entries()) {
        arr.sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
        map.set(k, arr);
    }

    return map;
});

const nonNullStorageEntries = (detail) => {
    const entries = []
    if (detail.warehouse) entries.push({ label: 'Warehouse', value: detail.warehouse })
    if (detail.container) entries.push({ label: 'Container', value: detail.container })
    if (detail.conveyor) entries.push({ label: 'Conveyor', value: detail.conveyor })
    return entries
}

const detailIdentifier = (location, detail) => {
    const locIdPart = String(location.identifier ?? location.id ?? location.locationId ?? '')
    const detailIdPart = String(detail.warehouse ?? detail.container ?? detail.conveyor ?? '')
    return `${locIdPart}_${detailIdPart}`
}
const getDetailsForLocation = (location) => {
    const locKey = String(location.id ?? location.locationId ?? '')
    return normalizedLocationDetailsMap.value.get(locKey) ?? []
}

const openLocations = ref(new Set())
const toggleLocation = (loc) => {
    const key = String(loc.id ?? loc.locationId ?? '')
    if (!key) return
    if (openLocations.value.has(key)) openLocations.value.delete(key)
    else openLocations.value.add(key)
    openLocations.value = new Set(openLocations.value)
}

const showDetailModal = ref(false)
const modalMode = ref('create')
const modalLocation = ref(null)
const modalDetail = ref(null)

const detailType = ref('warehouse')
const detailValue = ref('')

const firstNonNullEntry = (detail) => {
    if (detail.warehouse) return { label: 'warehouse', value: detail.warehouse }
    if (detail.container) return { label: 'container', value: detail.container }
    if (detail.conveyor) return { label: 'conveyor', value: detail.conveyor }
    return { label: 'warehouse', value: '' }
}

const resetDetailForm = () => {
    detailType.value = 'warehouse'
    detailValue.value = ''
}

const openAddDetail = (location) => {
    modalMode.value = 'create'
    modalLocation.value = location
    modalDetail.value = null
    resetDetailForm()
    showDetailModal.value = true
}

const openEditDetail = (location, detail) => {
    modalMode.value = 'edit'
    modalLocation.value = location
    modalDetail.value = detail
    const seed = firstNonNullEntry(detail)
    detailType.value = seed.label
    detailValue.value = String(seed.value ?? '')
    showDetailModal.value = true
}

const closeDetailModal = () => {
    showDetailModal.value = false
    modalLocation.value = null
    modalDetail.value = null
    resetDetailForm()
}

const saveDetail = () => {
    if (!modalLocation.value) return

    const payload = {
        locationId: modalLocation.value.id ?? modalLocation.value.locationId,
        warehouse: null,
        container: null,
        conveyor: null,
    }
    payload[detailType.value] = detailValue.value || null

    if (modalMode.value === 'create') {
        Inertia.post('/admin/database/location-details', payload, {
            preserveScroll: true,
            onSuccess: () => closeDetailModal(),
        })
    } else {
        const id = modalDetail.value?.id
        Inertia.post(`/admin/database/location-details/${id}`, { ...payload, _method: 'PUT' }, {
            preserveScroll: true,
            onSuccess: () => closeDetailModal(),
        })
    }
}

const deleteDetail = (detail) => {
    if (!confirm('Are you sure you want to delete this detail?')) return
    Inertia.delete(`/admin/database/location-details/${detail.id}`, {
        preserveScroll: true,
    })
}

const detailsCount = (location) => (getDetailsForLocation(location) || []).length
const activeTabLabel = computed(() => formatTabLabel(activeTab.value))
const sectionTitle = computed(() => {
    const tab = activeTab.value
    const fallback = `${activeTabLabel.value} Database`
    const ruleKey = `database.section_title.${tab}`

    return resolveTextRule(ruleKey, fallback, ruleKey)
})
const newItemButtonLabel = computed(() => {
    const tab = activeTab.value
    const fallback = `+ New ${activeTabLabel.value}`
    const ruleKey = `database.actions.new.${tab}`

    return resolveTextRule(ruleKey, fallback, ruleKey)
})
</script>

<template>
    <AppLayout title="Databases">
        <template #header>
            <h2 class="font-semibold text-xl text-gray-800 leading-tight">Database</h2>
        </template>

        <div class="mx-auto py-10 sm:px-6 lg:px-8">
            <!-- Tabs -->
            <div class="flex space-x-4 mb-6">
                <PrimaryButton :class="{ 'opacity-100': activeTab === 'color', 'opacity-50': activeTab !== 'color' }"
                    @click="changeTab('color')">{{ formatTabLabel('color') }}</PrimaryButton>
                <PrimaryButton
                    :class="{ 'opacity-100': activeTab === 'garment', 'opacity-50': activeTab !== 'garment' }"
                    @click="changeTab('garment')">{{ formatTabLabel('garment') }}</PrimaryButton>
                <PrimaryButton
                    :class="{ 'opacity-100': activeTab === 'fabric', 'opacity-50': activeTab !== 'fabric' }"
                    @click="changeTab('fabric')">{{ formatTabLabel('fabric') }}</PrimaryButton>
                <PrimaryButton
                    :class="{ 'opacity-100': activeTab === 'size', 'opacity-50': activeTab !== 'size' }"
                    @click="changeTab('size')">{{ formatTabLabel('size') }}</PrimaryButton>
                <PrimaryButton
                    :class="{ 'opacity-100': activeTab === 'location', 'opacity-50': activeTab !== 'location' }"
                    @click="changeTab('location')">{{ formatTabLabel('location') }}</PrimaryButton>
                <PrimaryButton
                    :class="{ 'opacity-100': activeTab === 'materialCode', 'opacity-50': activeTab !== 'materialCode' }"
                    @click="changeTab('materialCode')">{{ formatTabLabel('materialCode') }}</PrimaryButton>
                <PrimaryButton
                    :class="{ 'opacity-100': activeTab === 'department', 'opacity-50': activeTab !== 'department' }"
                    @click="changeTab('department')">{{ formatTabLabel('department') }}</PrimaryButton>
                <PrimaryButton
                    :class="{ 'opacity-100': activeTab === 'styleNumber', 'opacity-50': activeTab !== 'styleNumber' }"
                    @click="changeTab('styleNumber')">{{ formatTabLabel('styleNumber') }}</PrimaryButton>
            </div>

            <div class="flex justify-between items-center mb-4">
                <div class="text-lg font-semibold text-gray-700">
                    {{ sectionTitle }}
                </div>
                <PrimaryButton @click="goToCreate">
                    {{ newItemButtonLabel }}
                </PrimaryButton>
            </div>

            <div class="overflow-x-auto" v-if="activeTab !== 'location' && visibleItems().length > 0">
                <table class="min-w-full w-full text-left text-sm font-light">
                    <thead class="border-b">
                        <tr>
                            <th v-for="key in visibleKeys()" :key="key" class="px-4 py-2">
                                {{ resolveColumnLabel(activeTab, key) }}
                            </th>
                            <th class="px-4 py-2">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(item, index) in visibleItems()" :key="items()[index].id" class="border-b">
                            <td v-for="key in visibleKeys()" :key="key" class="px-4 py-2">
                                <img
                                    v-if="activeTab === 'styleNumber' && styleNumberExtendedView && key === 'image' && item[key]"
                                    :src="item[key]"
                                    alt="Equipment image"
                                    class="h-12 w-12 object-cover rounded border border-gray-200"
                                />
                                <span v-else-if="activeTab === 'styleNumber' && styleNumberExtendedView && key === 'image' && !item[key]" class="text-gray-400 italic">—</span>
                                <span v-else>{{ formatCellValue(activeTab, key, item[key]) }}</span>
                            </td>
                            <td class="px-4 py-2 space-x-2 text-lg">
                                <button class="fa fa-pen hover:text-blue-800"
                                    @click="() => Inertia.visit(route('admin.database.edit', { type: activeTab, id: items()[index].id }))"
                                    title="Edit"></button>
                                <button class="fa fa-trash ml-2" @click="confirmDelete(items()[index])" title="Delete"
                                    v-if="canDeleteActiveTab"></button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="overflow-x-auto" v-else-if="activeTab === 'location' && items().length > 0">
                <table class="min-w-full w-full text-sm table-fixed">
                    <colgroup>
                        <col class="w-12" />
                        <col class="w-1/2" />
                        <col class="w-1/4" />
                        <col class="w-1/4" />
                    </colgroup>

                    <thead class="border-b">
                        <tr>
                            <th class="px-4 py-2 text-left align-middle"></th>
                            <th class="px-4 py-2 text-left align-middle">Location Name</th>
                            <th class="px-4 py-2 text-center align-middle">Location Identifier</th>
                            <th class="px-4 py-2 text-right align-middle">Actions</th>
                        </tr>
                    </thead>

                    <tbody>
                        <template v-for="loc in items()" :key="loc.id">
                            <tr class="border-b">
                                <td class="px-4 py-2 align-middle">
                                    <button @click="toggleLocation(loc)"
                                        class="px-2 py-1 rounded border border-gray-300 hover:bg-gray-100 cursor-pointer inline-flex items-center justify-center"
                                        title="Toggle details">
                                        {{ openLocations.has(String(loc.id ?? loc.locationId)) ? '▼' : '▶' }}
                                    </button>
                                </td>

                                <td class="px-4 py-2 align-middle">
                                    <span class="whitespace-nowrap">{{ loc.name }}</span>
                                    <span class="ml-2 text-xs text-gray-500 align-middle">
                                        ( {{ detailsCount(loc) }} {{ detailsCount(loc) === 1 ? 'detail' : 'details' }} )
                                    </span>
                                </td>

                                <td class="px-4 py-2 text-center align-middle">
                                    <span class="font-mono">{{ loc.identifier }}</span>
                                </td>

                                <td class="px-4 py-2 align-middle">
                                    <div class="flex items-center justify-end gap-3">
                                        <button class="fa fa-plus hover:text-blue-800" @click="openAddDetail(loc)"
                                            title="Add detail"></button>
                                        <button class="fa fa-pen hover:text-blue-800"
                                            @click="() => Inertia.visit(route('admin.database.edit', { type: 'location', id: loc.id }))"
                                            title="Edit"></button>
                                        <button class="fa fa-trash hover:text-blue-800" @click="confirmDelete(loc)"
                                            title="Delete"></button>
                                    </div>
                                </td>
                            </tr>

                            <tr v-if="openLocations.has(String(loc.id ?? loc.locationId))" class="border-b">
                                <td></td>
                                <td class="px-4 py-3" colspan="3">
                                    <div v-if="getDetailsForLocation(loc).length > 0">
                                        <table class="min-w-full w-full text-sm table-fixed">
                                            <colgroup>
                                                <col class="w-1/2" />
                                                <col class="w-1/4" />
                                                <col class="w-1/4" />
                                            </colgroup>

                                            <thead class="border-b">
                                                <tr>
                                                    <th class="px-3 py-2 text-left align-middle">Location Detail</th>
                                                    <th class="px-3 py-2 text-center align-middle">Location Identifier
                                                    </th>
                                                    <th class="px-3 py-2 text-right align-middle">Actions</th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                <template v-for="detail in getDetailsForLocation(loc)" :key="detail.id">
                                                    <tr v-for="(entry, i) in nonNullStorageEntries(detail)"
                                                        :key="`${detail.id}-${i}`" class="border-b last:border-0">
                                                        <td class="px-3 py-2 align-middle">
                                                            <strong class="capitalize">{{ entry.label }}:</strong>
                                                            <span class="ml-1">{{ entry.value }}</span>
                                                        </td>

                                                        <td class="px-3 py-2 text-center align-middle">
                                                            <span class="font-mono text-xs">
                                                                {{ detailIdentifier(loc, detail) }}
                                                            </span>
                                                        </td>

                                                        <td class="px-3 py-2 align-middle">
                                                            <div class="flex items-center justify-end gap-3">
                                                                <button class="fa fa-pen hover:text-blue-800"
                                                                    @click="openEditDetail(loc, detail)"
                                                                    title="Edit detail"></button>
                                                                <button class="fa fa-trash hover:text-blue-800"
                                                                    @click="deleteDetail(detail)"
                                                                    title="Delete detail"></button>
                                                            </div>
                                                        </td>
                                                    </tr>

                                                    <tr v-if="nonNullStorageEntries(detail).length === 0"
                                                        :key="`empty-${detail.id}`" class="border-b last:border-0">
                                                        <td class="px-3 py-2 text-gray-400 italic" colspan="3">(Sin
                                                            valores no nulos)</td>
                                                    </tr>
                                                </template>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div v-else class="text-gray-500 italic">
                                        There are no details for this location
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>

            <div v-else class="text-gray-500 italic">No data to display.</div>
        </div>

        <DialogModal :show="showDeleteModal" @close="closeDeleteModal">
            <template #title>Confirm Deletion</template>
            <template #content>
                <p>Are you sure you want to delete <strong>{{ deleteItemLabel }}</strong>? This action
                    cannot be undone.</p>
            </template>
            <template #footer>
                <PrimaryButton class="text-gray-600 hover:text-gray-800 mr-4" @click="closeDeleteModal">Cancel
                </PrimaryButton>
                <PrimaryButton class="bg-red-600 hover:text-red-800 hover:bg-white font-semibold"
                    @click="performDelete">Delete</PrimaryButton>
            </template>
        </DialogModal>
        <DialogModal :show="showDetailModal" @close="closeDetailModal">
            <template #title>
                {{ modalMode === 'create' ? 'Adding' : 'Editing' }} location detail
                <span v-if="modalLocation"> for <strong>{{ modalLocation.name }}</strong></span>
            </template>

            <template #content>
                <div class="space-y-4">
                    <div>
                        <label class="block text-sm font-medium mb-1">Type</label>
                        <SelectInput v-model="detailType" class="py-1 px-2">
                            <option value="warehouse">warehouse</option>
                            <option value="container">container</option>
                            <option value="conveyor">conveyor</option>
                        </SelectInput>
                    </div>

                    <div>
                        <label class="block text-sm font-medium mb-1">ID / Value</label>
                        <input v-model="detailValue" type="text" class="w-full border rounded px-2 py-1"
                            placeholder="e.g. C11, DFW, CW..." />
                    </div>

                    <div v-if="modalLocation" class="text-xs text-gray-600">
                        Preview Identifier:
                        <code class="font-mono">
          {{ (modalLocation.identifier ?? modalLocation.id ?? modalLocation.locationId) + '_' + (detailValue || '…') }}
        </code>
                    </div>
                </div>
            </template>

            <template #footer>
                <PrimaryButton class="text-gray-600 hover:text-gray-800 mr-4" @click="closeDetailModal">Cancel
                </PrimaryButton>
                <PrimaryButton class="bg-green-600 hover:text-green-800 hover:bg-white font-semibold"
                    @click="saveDetail">
                    {{ modalMode === 'create' ? 'Add' : 'Save' }}
                </PrimaryButton>
            </template>
        </DialogModal>
    </AppLayout>
</template>
